import { useState, useCallback, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import type { CalendarSubscriptionDetail } from '../types';

/**
 * Feed fetches run on the worker, not in the request that triggers them:
 * subscribe/refresh return as soon as the fetch is enqueued, and the
 * events land seconds later. `awaitFetch` polls the subscription until
 * its `lastFetchedAt` advances (or the feed reports an error), so callers
 * can refetch events once there is actually something to show instead of
 * repainting an empty calendar.
 */
const FETCH_POLL_INTERVAL_MS = 1500;
const FETCH_POLL_TIMEOUT_MS = 45_000;

export function useCalendarSubscriptions() {
  const [subscriptions, setSubscriptions] = useState<CalendarSubscriptionDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const { subscriptions: data } = await api.listCalendarSubscriptions();
      setSubscriptions(data);
    } catch (err) {
      console.error('[calendar subscriptions] Failed to fetch', err);
      toast({
        title: 'Failed to load subscriptions',
        description: err instanceof Error ? err.message : 'Could not load calendar subscriptions',
        variant: 'destructive',
      });
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const subscribe = useCallback(async (feedUrl: string, name?: string) => {
    const result = await api.subscribeToCalendarFeed({ feedUrl, name });
    setSubscriptions((prev) => [result, ...prev]);
    return result;
  }, []);

  const refresh = useCallback(async (id: string) => {
    const result = await api.refreshCalendarSubscription(id);
    setSubscriptions((prev) => prev.map((s) => (s.id === id ? result : s)));
    return result;
  }, []);

  /**
   * Waits for the worker's fetch of `id` to complete. Resolves with the
   * settled subscription (fetched or errored), or null if it never
   * settled within the timeout — the caller refetches either way; a slow
   * feed just shows up on the next poll of the page.
   */
  const awaitFetch = useCallback(
    async (id: string, since: string | null): Promise<CalendarSubscriptionDetail | null> => {
      const deadline = Date.now() + FETCH_POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, FETCH_POLL_INTERVAL_MS));
        if (!mounted.current) return null;

        let data: CalendarSubscriptionDetail[];
        try {
          ({ subscriptions: data } = await api.listCalendarSubscriptions());
        } catch {
          continue; // transient — keep polling until the deadline
        }
        if (!mounted.current) return null;
        setSubscriptions(data);

        const sub = data.find((s) => s.id === id);
        if (!sub) return null; // unsubscribed while we waited
        if (sub.status === 'error' || sub.lastFetchedAt !== since) return sub;
      }
      return null;
    },
    [],
  );

  const unsubscribe = useCallback(async (id: string, mode: 'keep' | 'remove' = 'keep') => {
    const res = await api.unsubscribeCalendar(id, mode);
    setSubscriptions((prev) => prev.filter((s) => s.id !== id));
    return res;
  }, []);

  return {
    subscriptions,
    loading,
    fetchSubscriptions,
    subscribe,
    refresh,
    awaitFetch,
    unsubscribe,
  };
}
